import { Injectable, inject } from '@angular/core';

import { map, of } from 'rxjs';

import { ContextType } from '@camelot/services';

import { CamInvoicesService } from '../../features/invoice/services/invoices.service';
import { CamMaintenancesService } from '../../features/maintenance/services/maintenances.service';
import { CamProjectsService } from '../../features/project/services/projects.service';
import { CamQuotationsService } from '../../features/quotation/services/quotations.service';

export const contextTypeLabel = (contextType: ContextType) =>
  `project.tasks.contextType.${ContextType[contextType]?.toLowerCase() ?? 'unknown'}`;

@Injectable({
  providedIn: 'root',
})
export class CamTaskContextService {
  private _projectService = inject(CamProjectsService);
  private _invoiceService = inject(CamInvoicesService);
  private _quotationsService = inject(CamQuotationsService);
  private _maintenancesService = inject(CamMaintenancesService);

  public addContextNameToEntity<T extends { contextId?: string }>({
    ids,
    entities,
    contextType,
  }: {
    ids: string[];
    entities: {
      items?: T[];
    };
    contextType?: ContextType;
  }) {
    switch (contextType) {
      case ContextType.Project:
        return this._projectService.getProjectsLightInfo$(ids).pipe(
          map(data => ({
            ...entities,
            items: (entities.items ?? []).map(e => ({
              ...e,
              ...{ contextName: data.find(x => x.id === e.contextId)?.name },
            })),
          }))
        );
      case ContextType.Invoice:
        return this._invoiceService.getInvoicesLightInfo$(ids).pipe(
          map(data => ({
            ...entities,
            items: (entities.items ?? []).map(e => ({
              ...e,
              ...{ contextName: data.find(x => x.id === e.contextId)?.reference },
            })),
          }))
        );
      case ContextType.Quotation:
        return this._quotationsService.getVersionsLightInfo$(ids).pipe(
          map(data => ({
            ...entities,
            items: (entities.items ?? []).map(e => {
              const context = data.find(x => x.id === e.contextId);
              return {
                ...e,
                contextName: context ? `${context.quotation?.name} - ${context.version}` : '',
              };
            }),
          }))
        );
      case ContextType.Maintenance:
        return this._maintenancesService.getMaintenancesLightInfo$(ids).pipe(
          map(data => ({
            ...entities,
            items: (entities.items ?? []).map(e => ({
              ...e,
              ...{ contextName: data.find(x => x.id === e.contextId)?.title },
            })),
          }))
        );
      default:
        return of(entities);
    }
  }
}
